import { useState, useEffect } from 'react'

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

const DISMISS_KEY = 'orbit-pwa-dismiss'
const DISMISS_DAYS = 30

function isDismissed(): boolean {
  const raw = localStorage.getItem(DISMISS_KEY)
  if (!raw) return false
  const ts = Number(raw)
  if (Number.isNaN(ts)) return false
  return Date.now() - ts < DISMISS_DAYS * 86_400_000
}

function persistDismiss() {
  localStorage.setItem(DISMISS_KEY, String(Date.now()))
}

export function PwaInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [dismissed, setDismissed] = useState(isDismissed)

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault()
      setDeferredPrompt(e as BeforeInstallPromptEvent)
    }
    window.addEventListener('beforeinstallprompt', handler)
    return () => window.removeEventListener('beforeinstallprompt', handler)
  }, [])

  if (!deferredPrompt || dismissed) return null

  const handleInstall = async () => {
    await deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice
    if (outcome === 'accepted') setDeferredPrompt(null)
    else {
      persistDismiss()
      setDismissed(true)
    }
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-80 z-50 glass shadow-lg p-4 flex items-center gap-3 animate-slide-up">
      <div className="flex-shrink-0 p-2 bg-icg-primary/10 rounded-lg">
        <svg className="w-6 h-6 text-icg-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900">Install Orbit</p>
        <p className="text-xs text-gray-500">Add to home screen for quick access</p>
      </div>
      <div className="flex gap-2 flex-shrink-0">
        <button
          onClick={() => { persistDismiss(); setDismissed(true) }}
          className="text-xs text-gray-400 hover:text-gray-600 px-2 py-1.5 min-h-[36px]"
        >
          Later
        </button>
        <button
          onClick={handleInstall}
          className="text-xs font-medium text-white bg-icg-primary hover:bg-icg-primary/90 px-3 py-1.5 min-h-[36px] transition-colors"
        >
          Install
        </button>
      </div>
    </div>
  )
}
